import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";
import {
  ReportTypeItem,
  AxisChoice,
  GridData,
  FilterDefinition,
  ExecuteRequest,
  DetailRequest,
  DetailResponse,
  ActiveFilter,
  CannedReport,
  CannedReportArgs,
} from "../pages/grid-report/types";

class GridReportService {
  private baseUrl = `${Config.nodeSiteUrl}v1/grid-report`;

  /** Get all available report types */
  async getConfig(): Promise<ReportTypeItem[]> {
    const resp: any = await myAxios.get(`${this.baseUrl}/config`);
    return resp.data.data;
  }

  /** Get axis choices for a specific report type */
  async getAxisChoices(reportType: string): Promise<AxisChoice[]> {
    const resp: any = await myAxios.get(`${this.baseUrl}/axis-choices`, {
      params: { reportType },
    });
    return resp.data.data;
  }

  /** Get filter definitions with dropdown options */
  async getFilters(reportType: string): Promise<FilterDefinition[]> {
    const resp: any = await myAxios.get(`${this.baseUrl}/filters`, {
      params: { reportType },
    });
    return resp.data.data;
  }

  /** Execute a pivot report */
  async execute(request: ExecuteRequest): Promise<GridData> {
    const resp: any = await myAxios.post(`${this.baseUrl}/execute`, request);
    return resp.data.data;
  }

  /** Get drill-down detail rows for a cell */
  async getDetail(request: DetailRequest): Promise<DetailResponse> {
    const resp: any = await myAxios.post(`${this.baseUrl}/detail`, request);
    return resp.data.data;
  }

  /** Trigger a browser download for a blob response, honoring the
   *  server's Content-Disposition filename when present. */
  private downloadBlob(resp: any, fallbackName: string): void {
    const disposition: string = resp.headers?.["content-disposition"] || "";
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : fallbackName;
    const blob = new Blob([resp.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /** Export the full (unpaginated) pivot grid to Excel */
  async exportReport(request: ExecuteRequest): Promise<void> {
    const resp: any = await myAxios.post(`${this.baseUrl}/export`, request, {
      responseType: "blob",
      timeout: 120000,
    });
    this.downloadBlob(resp, "grid-report.xlsx");
  }

  /** Export all drill-down records for a cell to Excel */
  async exportDetail(
    request: DetailRequest & { xLabel?: string; yLabel?: string }
  ): Promise<void> {
    const resp: any = await myAxios.post(`${this.baseUrl}/export-detail`, request, {
      responseType: "blob",
      timeout: 120000,
    });
    this.downloadBlob(resp, "detail.xlsx");
  }
  /** List saved (canned) report bookmarks */
  async getCannedReports(): Promise<CannedReport[]> {
    const resp: any = await myAxios.get(`${this.baseUrl}/canned`);
    return resp.data.data;
  }

  /** Save a new canned report bookmark */
  async saveCannedReport(data: {
    displayLabel: string;
    description?: string;
    args: CannedReportArgs;
  }): Promise<{ id: number }> {
    const resp: any = await myAxios.post(`${this.baseUrl}/canned`, data);
    return resp.data.data;
  }

  /** Delete a canned report bookmark */
  async deleteCannedReport(id: number): Promise<void> {
    await myAxios.delete(`${this.baseUrl}/canned/${id}`);
  }

  /** AI-powered interpretation of full report data */
  async interpretReport(request: {
    reportType: string;
    xDimension: string;
    yDimension: string;
    startDate?: string;
    endDate?: string;
    filters?: ActiveFilter[];
  }): Promise<{
    interpretation: string;
    metadata: {
      model: string;
      processingTimeMs: number;
      dataSize: { rows: number; cols: number; legends: number };
      truncated: boolean;
    };
  }> {
    const resp: any = await myAxios.post(`${this.baseUrl}/interpret`, request, {
      timeout: 60000, // 60s — LLM calls can take a while
    });
    return resp.data.data;
  }

  // ===================== Admin: Reports Settings =====================

  /** Admin-only: all reports with effective display name / enabled / roles + role list. */
  async getAdminSettings(): Promise<{
    reports: ReportSettingRow[];
    roles: RoleOption[];
  }> {
    const resp: any = await myAxios.get(`${this.baseUrl}/admin/settings`);
    return resp.data.data;
  }

  /** Admin-only: update one report's display name / enabled flag / role guard / visible axes. */
  async updateReportSetting(
    key: string,
    payload: { displayName?: string; enabled?: boolean; roleIds?: number[]; axes?: string[] }
  ): Promise<void> {
    await myAxios.put(
      `${this.baseUrl}/admin/settings/${encodeURIComponent(key)}`,
      payload
    );
  }
}

export interface RoleOption {
  id: number;
  name: string;
}

export interface AxisOption {
  key: string;
  label: string;
}

export interface ReportSettingRow {
  key: string;
  defaultDisplayName: string;
  displayName: string;
  enabled: boolean;
  roleIds: number[];
  order: number;
  isComposite: boolean;
  /** All axes this report supports (for the X/Y dropdowns). */
  availableAxes: AxisOption[];
  /** Currently-visible axis keys (subset of availableAxes). */
  axes: string[];
}

export default new GridReportService();
